import { Apollo } from 'src/gql/generated';
import { themeV5 } from 'src/theme';

export interface DocStatusProperties {
  color: string;
  label: string;
  tooltipMsg: string;
}

// Type guard to check if value is DocumentStatus.
export function getIsDocumentStatus(status: unknown): status is Apollo.DocumentStatus {
  return (
    typeof status === 'string' &&
    Object.values(Apollo.DocumentStatus).some(statusEnum => status === statusEnum)
  );
}

export function getDocStatusLabelProps(
  value: Apollo.DocumentStatus | null = null
): DocStatusProperties {
  switch (value) {
    case Apollo.DocumentStatus.Draft:
      return {
        color: themeV5.appColors.primary,
        label: 'Draft',
        tooltipMsg: 'Document is in draft',
      };
    case Apollo.DocumentStatus.Signed:
      return {
        color: themeV5.appColors.accent2,
        label: 'Signed',
        tooltipMsg: 'Document is signed',
      };
    case Apollo.DocumentStatus.Template:
      return {
        color: themeV5.appColors.iconsType,
        label: 'Template',
        tooltipMsg: 'Document is a template',
      };
    case Apollo.DocumentStatus.Expired:
      return {
        color: themeV5.appColors.reddish,
        label: 'Expired',
        tooltipMsg: 'Document is expired',
      };
    default:
      // Do we need a valid color for Set Status?  accent1 is not correct (and we don't display it).
      return { color: themeV5.appColors.rule, label: 'No Status', tooltipMsg: 'No status found' };
  }
}

export interface StaticFileVersionWithLocation extends Pick<
  Apollo.StaticFileVersion,
  'id' | '__typename'
> {
  fileLocation: Pick<Apollo.DocFile, 'mimetype' | '__typename'>;
}
export interface FileLocationDocument extends Pick<Apollo.Document, 'id' | '__typename'> {
  latestVersion: Pick<Apollo.DocumentVersion, 'id' | '__typename'> | StaticFileVersionWithLocation;
}

/**
 * Returns the file location information for a Document, if it exists.
 */
export function getFileLocationFromDocument(
  document: FileLocationDocument
): StaticFileVersionWithLocation | null {
  return document.latestVersion.__typename === 'StaticFileVersion' ? document.latestVersion : null;
}

// Typename strings.
export const answerOptionsTypename: Apollo.AnswerOptions['__typename'] = 'AnswerOptions';
export const commentTypename: Apollo.Comment['__typename'] = 'Comment';
export const communityCategoryTypeName: Apollo.CommunityCategory['__typename'] =
  'CommunityCategory';
export const groupTypename: Apollo.Group['__typename'] = 'Group';
export const documentTypename: Apollo.Document['__typename'] = 'Document';
export const staticFileVersionTypename: Apollo.StaticFileVersion['__typename'] =
  'StaticFileVersion';
export const labelTypename: Apollo.Label['__typename'] = 'Label';
export const orgTypename: Apollo.Org['__typename'] = 'Org';
export const partyTypename: Apollo.Party['__typename'] = 'Party';
export const userSettingsTypename: Apollo.UserSettings['__typename'] = 'UserSettings';
export const reportTypename: Apollo.Report['__typename'] = 'Report';
export const agentThreadTypename: Apollo.AgentThread['__typename'] = 'AgentThread';
export const extractionQuestionTypename: Apollo.ExtractionQuestion['__typename'] =
  'ExtractionQuestion';
export const cannedFieldTypename: Apollo.CannedField['__typename'] = 'CannedField';
export const sigFieldTypename: Apollo.SignatureField['__typename'] = 'SignatureField';
export const stepTypename: Apollo.WorkflowStep['__typename'] = 'WorkflowStep';
export const taskTypename: Apollo.Task['__typename'] = 'Task';
export const userTypename: Apollo.User['__typename'] = 'User';
export const variableTypename: Apollo.DocumentVariable['__typename'] = 'DocumentVariable';
export const workflowTypename: Apollo.Workflow['__typename'] = 'Workflow';
export const nonSigningPhaseTypename: Apollo.NonSigningPhase['__typename'] = 'NonSigningPhase';
